import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Collapse,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  Link,
  Card,
  CardContent,
  LinearProgress
} from '@mui/material';
import {
  Email,
  Lock,
  Person,
  Phone,
  Visibility,
  VisibilityOff,
  PersonAdd,
  Psychology,
  CheckCircle,
  Cancel,
  Security,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import ReCAPTCHA from 'react-google-recaptcha';
import { useAuth } from '../contexts/AuthContext';
import { getDeviceFingerprint } from '../utils/deviceFingerprint';
import SocialLoginButtons from './SocialLoginButtons';
import api from '../api/client';
import ConsentModal from './legal/ConsentModal';
import SignupAgreementsSection from './legal/SignupAgreementsSection';
import AuthModeTabs from './auth/AuthModeTabs';
import { AUTH_PAGE_BG_SX } from './auth/authPageSx';
import { normalizeMobileNumber } from '../utils/mobileNumber';

const Register = ({ onSuccess, switchToLogin, initialReferralCode = '', postAuthRedirect = '/dashboard' }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    mobileNumber: '',
    referralCode: initialReferralCode
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [regDocuments, setRegDocuments] = useState([]);
  const [checkedDocs, setCheckedDocs] = useState({});
  const [viewDoc, setViewDoc] = useState(null);
  const [is13OrOlder, setIs13OrOlder] = useState(false);
  const [docsLoading, setDocsLoading] = useState(true);
  const [docsError, setDocsError] = useState(false);
  const [captchaRequired, setCaptchaRequired] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [attemptCount, setAttemptCount] = useState(0);
  const [showOptionalDetails, setShowOptionalDetails] = useState(Boolean(initialReferralCode));
  
  const { register, refreshPendingConsents } = useAuth();
  const recaptchaRef = useRef();

  useEffect(() => {
    setDocsLoading(true);
    setDocsError(false);
    api.legal.getFlowDocuments('registration')
      .then(({ data }) => setRegDocuments(data.documents || []))
      .catch(() => {
        setRegDocuments([]);
        setDocsError(true);
      })
      .finally(() => setDocsLoading(false));
  }, []);

  const hasAcceptedAllLegalDocs = regDocuments.length > 0
    ? regDocuments.every((d) => checkedDocs[d.id])
    : !!checkedDocs.fallback;

  const agreedToTerms = hasAcceptedAllLegalDocs;
  const canUseGoogleSignup = hasAcceptedAllLegalDocs && is13OrOlder && regDocuments.length > 0 && !docsLoading;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError(''); // Clear error when user types
    if (fieldErrors[e.target.name]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[e.target.name];
        return next;
      });
    }
  };

  // Password strength validation
  const getPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 6) strength += 1;
    if (password.length >= 8) strength += 1;
    if (/[A-Z]/.test(password)) strength += 1;
    if (/[a-z]/.test(password)) strength += 1;
    if (/[0-9]/.test(password)) strength += 1;
    if (/[^A-Za-z0-9]/.test(password)) strength += 1;
    return strength;
  };

  const passwordStrength = getPasswordStrength(formData.password);
  const passwordsMatch = formData.password === formData.confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setFieldErrors({});

    // Validation
    if (!agreedToTerms) {
      setError('Please agree to the Terms of Service and Privacy Policy');
      setLoading(false);
      return;
    }

    // COPPA age gate validation (13+ requirement)
    if (!is13OrOlder) {
      setError('You must confirm that you are 13 years old or older to create an account.');
      setLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (passwordStrength < 3) {
      setError('Password is too weak. Please use a stronger password.');
      setLoading(false);
      return;
    }

    // Check CAPTCHA requirement
    if (captchaRequired && !captchaToken) {
      setError('Please complete the security verification');
      setLoading(false);
      return;
    }

    try {
      // Include device fingerprint for enhanced rate limiting
      const deviceFingerprint = getDeviceFingerprint();
      
      // Prepare form data, excluding empty mobile number and referral code
      const registrationData = { ...formData };
      if (!registrationData.mobileNumber || registrationData.mobileNumber.trim() === '') {
        delete registrationData.mobileNumber;
      } else {
        registrationData.mobileNumber = normalizeMobileNumber(registrationData.mobileNumber);
        if (!registrationData.mobileNumber) {
          delete registrationData.mobileNumber;
        }
      }
      if (!registrationData.referralCode || registrationData.referralCode.trim() === '') {
        delete registrationData.referralCode;
      }
      
      const consentClickedAt = new Date().toISOString();
      const result = await register(registrationData, captchaToken, deviceFingerprint);

      if (result.success) {
        if (regDocuments.length > 0) {
          try {
            await api.legal.acceptConsent({
              documentIds: regDocuments.map((d) => d.id),
              consentMethod: 'signup_checkbox',
              consentClickedAt,
            });
            await refreshPendingConsents();
          } catch (consentErr) {
            console.warn('Post-registration consent recording failed:', consentErr);
          }
        }
        onSuccess && onSuccess(result.user, result.emailVerificationEnabled, result.devEmailHint);
      } else {
        setError(result.error);
        setFieldErrors(result.fieldErrors || {});
        
        // Handle rate limiting response
        if (result.captchaRequired) {
          setCaptchaRequired(true);
          setAttemptCount(result.attempts || 0);
        }
      }
    } catch (err) {
      setError('Registration failed. Please try again.');
      setAttemptCount(prev => prev + 1);
      
      // Require CAPTCHA after 2 failed attempts
      if (attemptCount >= 1) {
        setCaptchaRequired(true);
      }
    } finally {
      setLoading(false);
    }
  };

  // CAPTCHA handlers
  const handleCaptchaChange = (token) => {
    setCaptchaToken(token);
    setError(''); // Clear error when CAPTCHA is completed
  };

  const handleCaptchaExpired = () => {
    setCaptchaToken(null);
    setError('Security verification expired. Please try again.');
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength <= 2) return 'error';
    if (passwordStrength <= 4) return 'warning';
    return 'success';
  };

  const getPasswordStrengthText = () => {
    if (passwordStrength <= 2) return 'Weak';
    if (passwordStrength <= 4) return 'Medium';
    return 'Strong';
  };

  return (
    <Box sx={AUTH_PAGE_BG_SX}>
      <Card sx={{ maxWidth: 450, width: '100%', boxShadow: 3 }}>
        <CardContent sx={{ p: 4 }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Psychology sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
              Join JobMockAI
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Create your account and start mastering interviews with AI-powered coaching.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1.5 }}>
              Already have an account?{' '}
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={switchToLogin}
                sx={{ fontWeight: 700, verticalAlign: 'baseline' }}
              >
                Sign in
              </Link>
            </Typography>
          </Box>

          <AuthModeTabs mode="register" onLogin={switchToLogin} onRegister={() => {}} />

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Registration Form */}
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              name="name"
              label="Full Name"
              value={formData.name}
              onChange={handleChange}
              required
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Person color="action" />
                  </InputAdornment>
                )
              }}
            />

            <TextField
              fullWidth
              name="email"
              type="email"
              label="Email Address"
              value={formData.email}
              onChange={handleChange}
              required
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                )
              }}
            />

            <TextField
              fullWidth
              name="mobileNumber"
              type="tel"
              label="Mobile Number (Optional)"
              value={formData.mobileNumber}
              onChange={handleChange}
              sx={{ mb: 2 }}
              placeholder="+1 (555) 123-4567"
              error={Boolean(fieldErrors.mobileNumber)}
              helperText={fieldErrors.mobileNumber || ''}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone color="action" />
                  </InputAdornment>
                )
              }}
            />

            <Button
              type="button"
              fullWidth
              variant="text"
              onClick={() => setShowOptionalDetails((open) => !open)}
              endIcon={showOptionalDetails ? <ExpandLess /> : <ExpandMore />}
              sx={{ mb: 1, justifyContent: 'space-between', textTransform: 'none', color: 'text.secondary' }}
            >
              Have a referral code?
            </Button>
            <Collapse in={showOptionalDetails} unmountOnExit={!initialReferralCode}>
              <TextField
                fullWidth
                name="referralCode"
                label="Referral Code (Optional)"
                value={formData.referralCode}
                onChange={handleChange}
                sx={{ mb: 2 }}
                placeholder="Enter referral code"
                helperText={
                  initialReferralCode && formData.referralCode === initialReferralCode
                    ? '✓ Referral code applied — you\'ll earn bonus points on sign-up!'
                    : 'Have a referral code? Enter it here to earn bonus points!'
                }
                color={initialReferralCode && formData.referralCode === initialReferralCode ? 'success' : undefined}
                focused={initialReferralCode ? true : undefined}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonAdd color={initialReferralCode && formData.referralCode === initialReferralCode ? 'success' : 'action'} />
                    </InputAdornment>
                  )
                }}
              />
            </Collapse>

            <TextField
              fullWidth
              name="password"
              type={showPassword ? 'text' : 'password'}
              label="Password"
              value={formData.password}
              onChange={handleChange}
              required
              sx={{ mb: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            {/* Password Strength Indicator */}
            {formData.password && (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Password Strength:
                  </Typography>
                  <Typography 
                    variant="caption" 
                    color={`${getPasswordStrengthColor()}.main`}
                    sx={{ fontWeight: 'bold' }}
                  >
                    {getPasswordStrengthText()}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={(passwordStrength / 6) * 100}
                  color={getPasswordStrengthColor()}
                  sx={{ height: 6, borderRadius: 3 }}
                />
              </Box>
            )}

            <TextField
              fullWidth
              name="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              label="Confirm Password"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              sx={{ mb: 2 }}
              error={formData.confirmPassword && !passwordsMatch}
              helperText={formData.confirmPassword && !passwordsMatch ? 'Passwords do not match' : ''}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                    {formData.confirmPassword && (
                      passwordsMatch ? 
                        <CheckCircle color="success" sx={{ ml: 1 }} /> : 
                        <Cancel color="error" sx={{ ml: 1 }} />
                    )}
                  </InputAdornment>
                )
              }}
            />

            {/* CAPTCHA Component */}
            {captchaRequired && (
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Security sx={{ mr: 1, fontSize: 16 }} />
                    Security verification required
                  </Typography>
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"} // Test key for development
                    onChange={handleCaptchaChange}
                    onExpired={handleCaptchaExpired}
                    theme="light"
                  />
                  {attemptCount > 0 && (
                    <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
                      Failed attempts: {attemptCount}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}

            {/* Shared agreements — required for email AND Google signup */}
            <Box sx={{ mt: 2, mb: 2 }}>
              <SignupAgreementsSection
                regDocuments={regDocuments}
                checkedDocs={checkedDocs}
                onCheckChange={(id, val) => setCheckedDocs((prev) => ({ ...prev, [id]: val }))}
                is13OrOlder={is13OrOlder}
                onAgeChange={setIs13OrOlder}
                onViewDocument={setViewDoc}
                docsLoading={docsLoading}
                docsError={docsError}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading || !agreedToTerms || !is13OrOlder || !passwordsMatch || passwordStrength < 3 || (captchaRequired && !captchaToken)}
                startIcon={<PersonAdd />}
                sx={{
                  mt: 2,
                  mb: 1,
                  py: 1.5,
                  background: 'linear-gradient(135deg, #0D47A1 0%, #1565C0 100%)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #0a3d8f 30%, #002171 90%)',
                  },
                }}
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </Button>

              <SocialLoginButtons
                disabled={loading}
                variant="signup"
                referralCode={formData.referralCode}
                consentGranted={canUseGoogleSignup}
                consentDocumentIds={regDocuments.map((d) => d.id)}
                docsLoading={docsLoading}
                embedded
                hideLegalCaption
                postAuthRedirect={postAuthRedirect}
              />
            </Box>
          </Box>

          {/* Free Tier Benefits */}
          <Box sx={{ mt: 4, p: 2, bgcolor: 'success.50', borderRadius: 1, border: 1, borderColor: 'success.200' }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: 'success.main' }}>
              🎉 Free Account Includes:
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
              • 3 AI-powered interviews per month<br/>
              • Basic feedback and scoring<br/>
              • Access to 20+ popular job roles<br/>
              • Voice interview practice<br/>
              • Progress tracking dashboard
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <ConsentModal
        open={!!viewDoc}
        document={viewDoc}
        onClose={() => setViewDoc(null)}
        onAccept={(doc) => {
          setCheckedDocs((prev) => ({ ...prev, [doc.id]: true }));
          setViewDoc(null);
        }}
      />
    </Box>
  );
};

export default Register;